import React, { FC, useMemo, useState, useEffect } from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

interface TrendData {
  value: number;
  timestamp: string;
}

interface TrendLineChartProps {
  data: TrendData[];
  width?: number;
  height?: number;
  positiveColor?: string;
  negativeColor?: string;
  neutralColor?: string;
  lineColor?: string;
}

export const TrendLineChart: FC<TrendLineChartProps> = ({
  data,
  width = 80,
  height = 30,
  positiveColor = '#22C55E', // green
  negativeColor = '#EF4444', // red
  neutralColor = '#FCD34D', // yellow
  lineColor,
}) => {
  // State for line glow effect
  const [lineGlowIntensity, setLineGlowIntensity] = useState(8);
  
  // Sort data by timestamp
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [data]);

  // Determine trend direction
  const trendDirection = useMemo(() => {
    if (sortedData.length < 2) return 'neutral';
    
    const firstValue = sortedData[0].value;
    const lastValue = sortedData[sortedData.length - 1].value;
    
    if (lastValue > firstValue) return 'positive';
    if (lastValue < firstValue) return 'negative';
    return 'neutral';
  }, [sortedData]);

  // Determine color based on trend direction
  const color = useMemo(() => {
    if (lineColor) return lineColor;
    
    switch (trendDirection) {
      case 'positive': return positiveColor;
      case 'negative': return negativeColor;
      default: return neutralColor;
    }
  }, [trendDirection, lineColor, positiveColor, negativeColor, neutralColor]);

  // Effect to handle the line glow animation
  useEffect(() => {
    // Start with full glow intensity
    setLineGlowIntensity(8);
    
    // Fade out the glow after animation completes
    const fadeTimeout = setTimeout(() => {
      // Gradually fade out the glow
      const fadeInterval = setInterval(() => {
        setLineGlowIntensity(prev => {
          const newIntensity = prev - 0.5;
          if (newIntensity <= 0) {
            clearInterval(fadeInterval);
            return 0;
          }
          return newIntensity;
        });
      }, 50);
      
      return () => clearInterval(fadeInterval);
    }, 1500);
    
    return () => clearTimeout(fadeTimeout);
  }, [data]);

  if (!data || data.length === 0) {
    return <div style={{ width, height }} className="text-center text-gray-400">No data</div>;
  }

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={sortedData}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        >
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={true}
            animationDuration={1500}
            animationEasing="ease-in-out"
            filter="url(#trendLineGlowFilter)"
          />
          <defs>
            <filter id="trendLineGlowFilter" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceAlpha" stdDeviation={lineGlowIntensity} result="blur" />
              <feFlood floodColor="#00FF84" floodOpacity="0.6" result="color" />
              <feComposite in="color" in2="blur" operator="in" result="shadow" />
              <feMerge>
                <feMergeNode in="shadow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}; 